import { Modal, ModalFuncProps } from 'antd';
import { useCallback, useContext, useEffect, useRef } from 'react';
import type { Navigator } from 'react-router-dom';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { GLOB } from "src/util/Glob";

interface ConfirmExitOptions {
  message: string;
  modalProps?: Partial<ModalFuncProps>;
}

let isConfirmExitModalOpen = false;
const ConfirmExitManager = {
  open: async (message: string, modalProps: Partial<ModalFuncProps> = {}) => {
    if (isConfirmExitModalOpen) return false;
    isConfirmExitModalOpen = true;

    return new Promise<boolean>((resolve) => {
      const modal = GLOB.app.modal.confirm({
        title: 'Exit without saving?',
        content: message,
        okText: 'Leave',
        cancelText: 'Stay',
        onOk: () => {
          ConfirmExitManager.closeAll();
          resolve(true);
        },
        onCancel: () => {
          isConfirmExitModalOpen = false;
          resolve(false);
          modal.destroy();
        },
        ...modalProps,
      });
    });
  },
  closeAll: () => {
    Modal.destroyAll();
    isConfirmExitModalOpen = false;
  },
};

let isNavigatorPatched = false;
function useConfirmExit(confirmExit: () => Promise<boolean>, when = true): void {
  const { navigator } = useContext(NavigationContext);
  const originalPushRef = useRef<typeof navigator.push>();

  useEffect(() => {
    if (!when || isNavigatorPatched) return;
    isNavigatorPatched = true;

    // Store original push function with proper binding
    if (!originalPushRef.current) {
      originalPushRef.current = navigator.push.bind(navigator);
    }

    // Create new push function with confirmation
    const boundPush = originalPushRef.current;
    navigator.push = async (...args: Parameters<Navigator['push']>) => {
      if (isConfirmExitModalOpen) return;

      const result = await confirmExit();
      if (result !== false && boundPush) {
        boundPush(...args);
      }
    };

    // Cleanup function to restore original push
    return () => {
      if (originalPushRef.current) {
        navigator.push = originalPushRef.current;
        isNavigatorPatched = false;
      }
    };
  }, [navigator, confirmExit, when]);
}

export function usePrompt({ message, modalProps = {} }: ConfirmExitOptions, when: boolean): void {
  // Handle browser's native beforeunload event
  useEffect(() => {
    if (when) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = message;
        return message;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [message, when]);

  // Handle in-app navigation confirmation
  const confirmExit = useCallback(
    () => ConfirmExitManager.open(message, modalProps),
    [message, modalProps]
  );

  useConfirmExit(confirmExit, when);
}
